import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Icon,
  IconButton,
  Label,
  Modal,
  Text,
  TextButton,
  Toast,
  Tooltip,
  useToast,
} from '@zillow/constellation-10';
import { IconArchiveFilled, IconBuildingFilled, IconCopyFilled, IconPlusFilled } from '@zillow/constellation-icons';

import { LoadingText } from '@zg-rentals/loading-components';
import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

import { useMultiplexUnitEdit } from '../../../hooks';
import { getSquareFeetError } from '../../../utils/formValidation';
import { createEmptyRow, isDuplicateUnitNumber, MAX_UNIT_NUMBER_LENGTH } from '../../../utils/unitRowUtils';

import { MAX_NUM_MULTIPLEX_UNITS_VALUE } from '../../../constants/AddPropertyConstants';

import EditableUnitGrid from '../../modals/AddPropertyFlowModal/steps/EditableUnitGrid';
import FlexibleArchivePropertyModal, { ARCHIVE_TARGET_NOUN } from '../FlexibleArchivePropertyModal';

import EmptyState from '../../EmptyState';

import type {
  RentalManagerAddressDto,
  UserPropertyMultiplexUnitResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { UnitRow } from '../../../utils/unitRowUtils';

type Props = {
  isOpen: boolean;
  groupId: string;
  fullAddress: string;
  address: RentalManagerAddressDto;
  unitCount?: number;
  onClose: () => void;
  onSuccess: () => void;
};

const EMPTY_VALUE = '—';

const bedroomCountToString = (count: number | undefined | null): string => {
  if (count == null) return EMPTY_VALUE;
  return count === 0 ? 'Studio' : String(count);
};

const bathroomCountToString = (count: number | undefined | null): string => {
  if (count == null) return EMPTY_VALUE;
  return String(count);
};

const squareFeetToString = (squareFeet: number | undefined | null): string => {
  if (squareFeet == null || squareFeet === 0) return EMPTY_VALUE;
  return `${squareFeet} sqft`;
};

const existingUnitToRow = (unit: UserPropertyMultiplexUnitResponseWebDto): UnitRow => ({
  id: crypto.randomUUID(),
  unitNumber: unit.unitNumber,
  beds: unit.bedroomCount === 0 ? 'studio' : unit.bedroomCount != null ? String(unit.bedroomCount) : '',
  baths: unit.bathroomCount != null ? String(unit.bathroomCount) : '',
  size: unit.squareFeet ? String(unit.squareFeet) : '',
});

const ExistingUnitsSkeleton = ({ count }: { count: number }) => (
  <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
    {Array.from({ length: count }, (_, i) => (
      <Box
        key={i}
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr 1fr', md: '1fr 1fr 1fr 1fr 48px 48px' },
          columnGap: 'default',
          alignItems: 'center',
        }}
      >
        <Box css={{ gridColumn: { base: '1 / span 2', md: 'auto' } }}>
          <LoadingText isLoading={true} loadingWidth="80%">
            &nbsp;
          </LoadingText>
        </Box>
        <Box>
          <LoadingText isLoading={true} loadingWidth="60%">
            &nbsp;
          </LoadingText>
        </Box>
        <Box>
          <LoadingText isLoading={true} loadingWidth="60%">
            &nbsp;
          </LoadingText>
        </Box>
        <Box>
          <LoadingText isLoading={true} loadingWidth="70%">
            &nbsp;
          </LoadingText>
        </Box>
        <Box css={{ gridColumn: { base: 'auto', md: 'span 2' } }}>
          <LoadingText isLoading={true} loadingWidth="48px">
            &nbsp;
          </LoadingText>
        </Box>
      </Box>
    ))}
  </Box>
);

const EditMultiplexPropertyModal = ({
  isOpen,
  groupId,
  fullAddress,
  address,
  unitCount,
  onClose,
  onSuccess,
}: Props): JSX.Element => {
  const [newRows, setNewRows] = useState<Array<UnitRow>>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [archiveTargetAlias, setArchiveTargetAlias] = useState<string | null>(null);

  const { enqueueToast } = useToast();
  const { existingUnits, existingUnitNumbers, isLoadingUnits, isErrorUnits, isPending, createUnits } =
    useMultiplexUnitEdit({ groupId, fullAddress });

  const isAtMax = existingUnits.length + newRows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE;
  const maxNewUnits = MAX_NUM_MULTIPLEX_UNITS_VALUE - existingUnits.length;
  const hasSizeError = newRows.some((row) => Boolean(getSquareFeetError(row.size)));

  const handleAddRow = () => {
    if (isAtMax) return;
    setHasSubmitted(false);
    setNewRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleCopyExistingUnit = (unit: UserPropertyMultiplexUnitResponseWebDto) => {
    if (isAtMax) return;
    setNewRows((prev) => [...prev, existingUnitToRow(unit)]);
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (existingUnits.length + newRows.length > MAX_NUM_MULTIPLEX_UNITS_VALUE) return;
    const hasErrors = newRows.some(
      (row) =>
        !row.unitNumber.trim() ||
        row.unitNumber.length > MAX_UNIT_NUMBER_LENGTH ||
        isDuplicateUnitNumber(row.id, row.unitNumber, newRows, existingUnitNumbers) ||
        !row.beds ||
        !row.baths ||
        Boolean(getSquareFeetError(row.size)),
    );
    if (hasErrors) return;

    createUnits(
      newRows.map((row) => ({
        unitNumber: row.unitNumber,
        bedroomCount: row.beds === 'studio' ? 0 : row.beds ? Number(row.beds) : undefined,
        bathroomCount: row.baths ? parseFloat(row.baths) : undefined,
        squareFeet: row.size ? Number(row.size) : undefined,
      })),
      {
        onSuccess: (result) => {
          if (result.successfulUnits.length === 0) {
            setErrorMessage('No units were saved. Try again.');
            return;
          }
          if (result.hasPartialUnitsSucceeded) {
            const failed = result.failedUnits.map((u) => `Unit ${u.unitNumber}: ${u.errorMessage}`).join(' ');
            setErrorMessage(failed);
          }
          enqueueToast(<Toast tone="success">Units saved</Toast>);
          onSuccess();
        },
        onError: (msg) => setErrorMessage(msg),
      },
    );
  };

  const renderExistingUnitsContent = () => {
    if (isLoadingUnits) {
      return <ExistingUnitsSkeleton count={unitCount ?? 3} />;
    }

    if (isErrorUnits) {
      return (
        <Alert role="status" tone="critical">
          Existing units couldn’t be loaded. Try again.
        </Alert>
      );
    }

    if (existingUnits.length === 0) {
      return (
        <EmptyState
          icon={<Icon size="md" css={{ color: 'icon.subtle' }} render={<IconBuildingFilled />} />}
          heading="No units yet"
        />
      );
    }

    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
        <Box
          css={{
            display: { base: 'none', md: 'grid' },
            gridTemplateColumns: '1fr 1fr 1fr 1fr 48px 48px',
            columnGap: 'default',
            alignItems: 'end',
          }}
        >
          <Label aria-hidden="true" required>
            Unit number
          </Label>
          <Label aria-hidden="true" required>
            Beds
          </Label>
          <Label aria-hidden="true" required>
            Baths
          </Label>
          <Label aria-hidden="true" optional>
            Size
          </Label>
          <Box aria-hidden="true" css={{ gridColumn: 'span 2' }}>
            <Label>Options</Label>
          </Box>
        </Box>

        {existingUnits.map((unit) => (
          <Box
            key={unit.unitNumber}
            data-testid="existing-unit-row"
            css={{
              display: 'grid',
              gridTemplateColumns: { base: '1fr 1fr', md: '1fr 1fr 1fr 1fr 48px 48px' },
              columnGap: 'default',
              alignItems: 'center',
            }}
          >
            <Box css={{ gridColumn: { base: '1 / span 2', md: 'auto' } }}>
              <Text textStyle="body">{unit.unitNumber}</Text>
            </Box>
            <Box>
              <Text textStyle="body">{bedroomCountToString(unit.bedroomCount)}</Text>
            </Box>
            <Box>
              <Text textStyle="body">{bathroomCountToString(unit.bathroomCount)}</Text>
            </Box>
            <Box>
              <Text textStyle="body">{squareFeetToString(unit.squareFeet)}</Text>
            </Box>
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'flex-start',
                gridColumn: { base: 'auto', md: 'span 2' },
              }}
            >
              <ButtonGroup aria-label="Unit options">
                <Tooltip.Root>
                  <Tooltip.Trigger>
                    <IconButton
                      data-testid="copy-existing-unit-button"
                      title="Duplicate"
                      size="md"
                      tone="neutral"
                      emphasis="bare"
                      icon={<IconCopyFilled />}
                      disabled={isAtMax || isLoadingUnits}
                      onClick={() => handleCopyExistingUnit(unit)}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content>
                      Duplicate
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
                <Tooltip.Root>
                  <Tooltip.Trigger>
                    <IconButton
                      data-testid="archive-existing-unit-button"
                      title="Archive"
                      size="md"
                      tone="critical"
                      emphasis="bare"
                      icon={<IconArchiveFilled />}
                      onClick={() => setArchiveTargetAlias(unit.listingAlias)}
                      css={{ marginLeft: 'auto' }}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content>
                      Archive
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </ButtonGroup>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {archiveTargetAlias && (
        <FlexibleArchivePropertyModal
          onClose={() => setArchiveTargetAlias(null)}
          isArchived={false}
          listingAlias={archiveTargetAlias}
          groupId={groupId}
          address={address}
          targetNoun={ARCHIVE_TARGET_NOUN.UNIT}
          listingType={AccountViewListingGroupType.LISTING}
        />
      )}
      <Modal
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        size={{ base: 'fullScreen', md: 'xl' }}
        header={<Heading level={2}>Edit property</Heading>}
        body={
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
            {errorMessage && (
              <Alert role="status" tone="critical">
                {errorMessage}
              </Alert>
            )}

            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
              {(isLoadingUnits || isErrorUnits || existingUnits.length > 0) && (
                <Text textStyle="body-bold">
                  Units (
                  <LoadingText isLoading={isLoadingUnits} loadingWidth="2ch">
                    {existingUnits.length}
                  </LoadingText>
                  )
                </Text>
              )}
              {renderExistingUnitsContent()}
            </Box>

            {newRows.length > 0 && (
              <>
                <Divider />
                <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
                  <Text textStyle="body-bold">New units ({newRows.length})</Text>
                  <EditableUnitGrid
                    rows={newRows}
                    onChange={setNewRows}
                    existingUnitNumbers={existingUnitNumbers}
                    hasSubmitted={hasSubmitted}
                    maxUnits={maxNewUnits}
                    minRows={0}
                  />
                </Box>
              </>
            )}

            <Button
              data-testid="add-unit-button"
              fluid={false}
              tone="brand"
              emphasis="outlined"
              icon={<IconPlusFilled />}
              disabled={isAtMax || isLoadingUnits}
              onClick={handleAddRow}
              css={{ alignSelf: 'flex-start' }}
            >
              Add unit
            </Button>
          </Box>
        }
        footer={
          <ButtonGroup aria-label="Edit property actions">
            <TextButton tone="brand" onClick={onClose}>
              Cancel
            </TextButton>
            <Button
              data-testid="save-changes-button"
              fluid={false}
              tone="brand"
              emphasis="filled"
              loading={isPending}
              disabled={newRows.length === 0 || hasSizeError}
              onClick={handleSubmit}
            >
              Save changes
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
};

export default EditMultiplexPropertyModal;
